import React, { useState, useRef, useEffect, useCallback, useMemo, useReducer } from 'react';
import { Grid, Button, Image, Icon } from 'semantic-ui-react';
import { getResourceUrl } from '../utils/pathRegistryUtils';
import { IMAGE_CONFIG } from '../constants/imageConfig';
import useMediaUpload from '../hooks/useMediaUpload';
import '../styles/avatarStyles.css';

/**
 * Memoized Avatar Image Component
 * Only re-renders when src or visibility changes
 */
const MemoizedAvatarImage = ({
  src,
  isVisible,
  sizeClass,
  imageType,
  onLoad,
  onError,
  imageRef
}) => {

  const visibilityClass = isVisible ? 'visible' : 'hidden';
  const className = `avatar-image avatar-image-${imageType} ${sizeClass} ${visibilityClass}`;

  return (
    <Image
      ref={imageRef}
      src={src}
      circular
      className={className}
      onLoad={onLoad}
      onError={onError}
    />
  );
};

MemoizedAvatarImage.displayName = 'MemoizedAvatarImage';

/**
 * Enhanced AvatarPicker with Dual Image System
 * Uses two images (A and B) to eliminate flickering during transitions
 * @param {Object} props - Component props
 * @param {string} props.currentAvatarUrl - Current avatar URL
 * @param {string} props.characterId - Character ID for state cleanup
 * @param {Function} props.onAvatarChange - Callback when avatar changes
 * @param {Function} props.onUploadComplete - Callback when upload completes
 * @param {Function} props.onUploadError - Callback when upload fails
 * @param {Object} props.uploadParams - Parameters for upload
 * @param {string} props.resourceType - Resource type for Path Registry
 * @param {string} props.size - Size of avatar (small, medium, large)
 * @param {boolean} props.showHints - Whether to show status hints
 * @param {string} props.hintLocation - Whether to show status hints
 * @param {boolean} props.autoUpload - Upload immediately vs on form submit
 * @param {boolean} props.disabled - Disable the picker
 * @param {string} props.uploadStatus - Upload status from parent
 * @param {Object} props.uploadResult - Upload result from parent
 * @returns {JSX.Element} - Rendered component
 */

const AvatarPicker = ({
  currentAvatarUrl,
  characterId,
  onAvatarChange,
  onUploadComplete,
  onUploadError,
  uploadParams = {},
  resourceType = null,
  size = 'large',
  showHints = true,
  hintLocation = "middle",
  autoUpload = false,
  disabled = false,
  uploadStatus = 'idle',
  uploadResult = null
}) => {
  // Upload hook
  const { uploadMedia, isUploading, error: uploadError, reset: resetUpload } = useMediaUpload();

  // Helper functions for creating hints
  const createHint = (mode, autoUpload = false, message = null) => {
    switch (mode) {
      case 'idle':
        return {
          mode: 'idle',
          message: 'Click or drag image to change avatar',
          icon: 'camera',
          color: '#767676',
          loading: false
        };
      case 'ready':
        return {
          mode: 'ready',
          message: autoUpload ? 'Avatar ready to upload' : 'Avatar will upload when you save',
          icon: 'check',
          color: '#21ba45',
          loading: false
        };
      case 'uploading':
        return {
          mode: 'uploading',
          message: 'Uploading avatar...',
          icon: 'spinner',
          color: '#2185d0',
          loading: true
        };
      case 'success':
        return {
          mode: 'success',
          message: 'Avatar updated successfully',
          icon: 'check',
          color: '#21ba45',
          loading: false
        };
      case 'error':
        return {
          mode: 'error',
          message: message || 'Upload failed',
          icon: 'exclamation triangle',
          color: '#db2828',
          loading: false
        };
      case 'drag_over':
        return {
          mode: 'drag_over',
          message: 'Drop image to upload',
          icon: 'cloud upload',
          color: '#2185d0',
          loading: false
        };
      case 'drag_invalid':
        return {
          mode: 'drag_invalid',
          message: message || 'Invalid file type',
          icon: 'ban',
          color: '#db2828',
          loading: false
        };
      default:
        return createHint('idle');
    }
  };

  // Unified avatar state reducer
  const avatarReducer = (state, action) => {
    console.log('[AVATAR_REDUCER] Action:', action.type, action.payload);

    switch (action.type) {
      case 'DRAG_ENTER':
        return {
          ...state,
          isDragOver: true,
          hint: createHint('drag_over')
        };

      case 'DRAG_LEAVE':
        return {
          ...state,
          isDragOver: false,
          hint: state.previewUrl ? createHint('ready', state.autoUpload) : createHint('idle')
        };

      case 'DRAG_DROP':
        return {
          ...state,
          isDragOver: false
        };

      case 'DRAG_INVALID':
        return {
          ...state,
          isDragOver: false,
          hint: createHint('drag_invalid', false, action.payload.message)
        };

      case 'FILE_SELECTED':
        const { slot, source, autoUpload } = action.payload;
        const slotKey = `slot${slot}`;
        return {
          ...state,
          slots: {
            ...state.slots,
            [slotKey]: {
              ...state.slots[slotKey],
              source: source
            },
            loadingSlot: slot
          },
          hint: createHint('ready', autoUpload),
          previewUrl: source
        };

      case 'SLOT_LOADED':
        const { loadedSlot } = action.payload || {};
        if (!loadedSlot) {
          console.warn('[AVATAR_REDUCER] SLOT_LOADED called without loadedSlot');
          return {
            ...state,
            slots: {
              ...state.slots,
              loadingSlot: null
            }
          };
        }

        // Only activate slot if it was being loaded with new content
        if (state.slots.loadingSlot === loadedSlot &&
          state.slots[`slot${loadedSlot}`]?.source !== IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl &&
          !state.slots[`slot${loadedSlot}`]?.source?.includes('/narrator_1-1-480x490.png')) {
          return {
            ...state,
            slots: {
              ...state.slots,
              slotA: { ...state.slots.slotA, isVisible: loadedSlot === 'A' },
              slotB: { ...state.slots.slotB, isVisible: loadedSlot === 'B' },
              activeSlot: loadedSlot,
              loadingSlot: null
            }
          };
        }
        return {
          ...state,
          slots: {
            ...state.slots,
            loadingSlot: null
          }
        };

      case 'UPLOAD_START':
        return {
          ...state,
          hint: createHint('uploading')
        };

      case 'UPLOAD_SUCCESS':
        const { result, uploadId } = action.payload;
        // Prevent processing the same upload multiple times
        if (state.processedUploads.has(uploadId)) {
          console.log('[AVATAR_REDUCER] Upload already processed:', uploadId);
          return state;
        }

        return {
          ...state,
          hint: createHint('success'),
          uploadResult: result,
          processedUploads: new Set([...state.processedUploads, uploadId]),
          pendingUpload: { result, uploadId }
        };

      case 'UPLOAD_COMPLETE':
        const { targetSlot, signedUrl: uploadSignedUrl } = action.payload;
        return {
          ...state,
          slots: {
            ...state.slots,
            [`slot${targetSlot}`]: {
              ...state.slots[`slot${targetSlot}`],
              source: uploadSignedUrl
            },
            loadingSlot: targetSlot
          },
          pendingUpload: null
        };

      case 'UPLOAD_FINALIZED':
        const { finalSlot } = action.payload;
        return {
          ...state,
          slots: {
            ...state.slots,
            slotA: { ...state.slots.slotA, isVisible: finalSlot === 'A' },
            slotB: { ...state.slots.slotB, isVisible: finalSlot === 'B' },
            activeSlot: finalSlot,
            loadingSlot: null
          },
          hint: createHint('idle'),
          previewUrl: null,
          uploadResult: null
        };

      case 'UPLOAD_ERROR':
        return {
          ...state,
          hint: createHint('error', false, action.payload.message),
          uploadResult: null,
          pendingUpload: null
        };

      case 'CLEAR_LOADING':
        return {
          ...state,
          slots: {
            ...state.slots,
            loadingSlot: null
          }
        };

      case 'RESET_AVATAR':
        return {
          ...initialAvatarState,
          hint: createHint('idle')
        };

      case 'LOAD_INTO_SLOT':
        const { slot: loadSlot, source: loadSource } = action.payload;
        const loadSlotKey = `slot${loadSlot}`;
        return {
          ...state,
          slots: {
            ...state.slots,
            [loadSlotKey]: {
              ...state.slots[loadSlotKey],
              source: loadSource
            },
            loadingSlot: loadSlot
          }
        };

      case 'INITIALIZE_AVATAR':
        const { avatarUrl, initSlot } = action.payload;
        console.log('[AVATAR_REDUCER] Initializing avatar:', { avatarUrl, initSlot });
        const newSlots = {
          ...state.slots,
          activeSlot: initSlot,
          loadingSlot: initSlot
        };

        // Update the specific slot
        if (initSlot === 'A') {
          newSlots.slotA = {
            ...state.slots.slotA,
            source: avatarUrl,
            isVisible: true
          };
          newSlots.slotB = {
            ...state.slots.slotB,
            isVisible: false
          };
        } else {
          newSlots.slotB = {
            ...state.slots.slotB,
            source: avatarUrl,
            isVisible: true
          };
          newSlots.slotA = {
            ...state.slots.slotA,
            isVisible: false
          };
        }

        return {
          ...state,
          slots: newSlots
        };

      case 'INITIALIZE_AVATAR_WITH_SIGNED_URL':
        const { signedUrl: initSignedUrl, targetInitSlot } = action.payload;
        return {
          ...state,
          slots: {
            ...state.slots,
            [`slot${targetInitSlot}`]: {
              ...state.slots[`slot${targetInitSlot}`],
              source: initSignedUrl
            },
            slotA: { ...state.slots.slotA, isVisible: targetInitSlot === 'A' },
            slotB: { ...state.slots.slotB, isVisible: targetInitSlot === 'B' },
            activeSlot: targetInitSlot,
            loadingSlot: null // Clear loading state
          }
        };

      default:
        return state;
    }
  };

  // Initialize unified avatar state
  const initialAvatarState = {
    slots: {
      slotA: {
        source: IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl,
        isVisible: true
      },
      slotB: {
        source: IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl,
        isVisible: false
      },
      activeSlot: 'A',
      loadingSlot: null
    },
    hint: createHint('idle'),
    previewUrl: null,
    uploadResult: null,
    pendingUpload: null,
    processedUploads: new Set(),
    isDragOver: false
  };

  const [avatarState, dispatch] = useReducer(avatarReducer, initialAvatarState);
  const { slots, hint, previewUrl, pendingUpload, isDragOver } = avatarState;
  const { slotA, slotB, activeSlot, loadingSlot } = slots;

  // Debug logging to see actual state values
  console.log('[AVATAR_DEBUG] Current state values:', {
    slotA: slotA.source?.substring(0, 50) + '...',
    slotB: slotB.source?.substring(0, 50) + '...',
    activeSlot,
    loadingSlot,
    slotAVisible: slotA.isVisible,
    slotBVisible: slotB.isVisible,
    hintMode: hint.mode,
    pendingUpload: !!pendingUpload
  });

  // Refs
  const imageARef = useRef(null);
  const imageBRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevCharacterIdRef = useRef(characterId);
  const initializationDebounceRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  // Get user ID
  const userId = uploadParams.userId;

  // Helper functions
  const isSignedUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('X-Amz-Signature=');
  };

  const isS3Url = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('s3.') || url.includes('amazonaws.com') || url.includes('aai-users/');
  };

  // Slot-based load handlers with immediate timeout clearing
  const handleSlotLoad = useCallback((slot) => (e) => {
    console.log(`[SLOT_${slot}] Image loaded:`, e.target.src);

    // Clear the safety timeout immediately when image loads successfully
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
      console.log(`[SLOT_${slot}] Cleared safety timeout - image loaded successfully`);
    }

    dispatch({ type: 'SLOT_LOADED', payload: { loadedSlot: slot } });
  }, []);

  const handleSlotError = useCallback((slot) => (e) => {
    console.error(`[SLOT_${slot}] Image failed to load:`, e.target.src);

    // Clear the safety timeout immediately when image fails
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
      console.log(`[SLOT_${slot}] Cleared safety timeout - image failed to load`);
    }

    e.target.src = IMAGE_CONFIG.CHARACTER_AVATAR.defaultUrl;
    dispatch({ type: 'SLOT_LOADED', payload: { loadedSlot: slot } });
  }, []);

  // // Simplified load function with event-driven handling
  // const loadIntoSlot = useCallback((slot, source) => {
  //   console.log(`[SLOT_LOAD] Loading into slot ${slot}:`, source);

  //   // Clear any existing timeout
  //   if (loadingTimeoutRef.current) {
  //     clearTimeout(loadingTimeoutRef.current);
  //     loadingTimeoutRef.current = null;
  //   }

  //   dispatch({
  //     type: 'LOAD_INTO_SLOT',
  //     payload: { slot, source }
  //   });

  //   // Set a much shorter safety timeout (3 seconds) as a true safety net
  //   loadingTimeoutRef.current = setTimeout(() => {
  //     console.log(`[SLOT_TIMEOUT] Loading timeout for slot ${slot} after 3s, clearing loading state`);
  //     dispatch({ type: 'CLEAR_LOADING' });
  //     loadingTimeoutRef.current = null;
  //   }, 3000);
  // }, []);

  // Generate signed URL for upload result
  const generateSignedUrlForUpload = useCallback(async (uploadUrl) => {
    try {
      console.log('[AVATAR DUAL] Generating signed URL for upload result:', uploadUrl);

      if (isSignedUrl(uploadUrl)) {
        console.log('[AVATAR DUAL] Upload URL is already signed');
        return uploadUrl;
      }

      if (!isS3Url(uploadUrl)) {
        console.log('[AVATAR DUAL] Upload URL is not S3, using as-is');
        return uploadUrl;
      }

      // For existing S3 URLs, use the simpler getSignedUrlForS3Path
      if (uploadUrl.includes('amazonaws.com') || uploadUrl.includes('aai-users/')) {
        console.log('[AVATAR DUAL] S3 URL detected, generating signed URL using getSignedUrlForS3Path');

        try {
          const { getSignedUrlForS3Path } = await import('../utils/s3PathUtils');
          const signedUrl = await getSignedUrlForS3Path(uploadUrl, userId);

          console.log('[AVATAR DUAL] Successfully generated signed URL:', signedUrl);
          return signedUrl;
        } catch (error) {
          console.error('[AVATAR DUAL] Error with getSignedUrlForS3Path, falling back to path registry:', error);
          // Fall through to path registry approach
        }
      }

      // Fallback: Try path registry approach if needed
      if (resourceType && userId) {
        console.log('[AVATAR DUAL] Trying path registry approach as fallback');

        const { extractParamsFromPath } = await import('../utils/pathRegistryUtils');

        let path = '';
        if (uploadUrl.startsWith('http')) {
          const url = new URL(uploadUrl);
          path = url.pathname.substring(1); // Remove leading slash
        } else {
          path = uploadUrl;
        }

        const extractionResult = await extractParamsFromPath(path, resourceType);

        if (extractionResult.success) {
          // Extract the ACTUAL filename from the URL
          const actualFileName = uploadUrl.split('/').pop().split('?')[0];

          // Override the fileName with the actual filename we extracted
          const paramsWithCorrectFileName = {
            ...extractionResult.params,
            fileName: actualFileName
          };

          console.log('[AVATAR DUAL] Using corrected params:', paramsWithCorrectFileName);

          const signedUrl = await getResourceUrl(
            userId,
            resourceType,
            paramsWithCorrectFileName,
            actualFileName
          );

          console.log('[AVATAR DUAL] Generated signed URL via path registry:', signedUrl);
          return signedUrl;
        }
      }

      // Fallback to original URL
      console.log('[AVATAR DUAL] Using original URL as fallback');
      return uploadUrl;

    } catch (error) {
      console.error('[AVATAR DUAL] Error generating signed URL:', error);
      return uploadUrl; // Fallback to original
    }
  }, [resourceType, userId]);

  // Process dropped or selected file (shared logic)
  const processFile = useCallback((file) => {
    if (!file) return;

    console.log('[DRAG_DROP] Processing file:', file.name, file.type, file.size);

    // Clean up any existing preview URL to prevent memory leaks
    if (previewUrl && previewUrl.startsWith('blob:')) {
      console.log('[SLOT_PREVIEW] Cleaning up previous preview URL:', previewUrl);
      URL.revokeObjectURL(previewUrl);
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      dispatch({ type: 'DRAG_INVALID', payload: { message: 'Please select a valid image file' } });
      return;
    }

    // Validate file size
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      dispatch({ type: 'DRAG_INVALID', payload: { message: `File too large. Maximum size is 5MB. Current: ${(file.size / (1024 * 1024)).toFixed(2)}MB` } });
      return;
    }

    // Create preview URL
    const blobUrl = URL.createObjectURL(file);
    console.log('[SLOT_PREVIEW] File processed, creating preview:', blobUrl);

    // Use the inactive slot for preview
    const previewSlot = activeSlot === 'A' ? 'B' : 'A';
    console.log(`[SLOT_PREVIEW] Loading preview into slot ${previewSlot}`);

    dispatch({
      type: 'FILE_SELECTED',
      payload: { slot: previewSlot, source: blobUrl, autoUpload }
    });

    resetUpload();

    // Notify parent
    if (onAvatarChange) {
      onAvatarChange({
        url: blobUrl,
        file: file,
        isPreview: true,
        isUploaded: false,
        uploadStatus: 'ready',
        hasChanges: true
      });
    }

    // Auto-upload if enabled
    if (autoUpload) {
      performUpload(file);
    }
  }, [activeSlot, autoUpload, onAvatarChange, resetUpload, previewUrl]);

  // Drag event handlers
  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dispatch({ type: 'DRAG_ENTER' });
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    // Only dispatch if leaving the actual drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      dispatch({ type: 'DRAG_LEAVE' });
    }
  }, [disabled]);

  const handleDrop = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    console.log('[DRAG_DROP] Files dropped:', e.dataTransfer.files.length);
    dispatch({ type: 'DRAG_DROP' });

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (files.length > 1) {
        dispatch({ type: 'DRAG_INVALID', payload: { message: 'Please drop only one image file' } });
        return;
      }
      processFile(files[0]);
    }
  }, [disabled, processFile]);

  // Handle file selection (slot-based) - now uses shared processFile logic
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    processFile(file);
  }, [processFile]);

  // Perform upload
  const performUpload = useCallback(async (file) => {
    if (!autoUpload || !file) return;

    try {
      dispatch({ type: 'UPLOAD_START' });

      const uploadUrl = await uploadMedia(
        file,
        resourceType || 'bookCharacterAvatarImage',
        uploadParams,
        userId
      );

      const uploadId = uploadUrl + '_' + Date.now();
      dispatch({
        type: 'UPLOAD_SUCCESS',
        payload: { result: { url: uploadUrl }, uploadId }
      });

      if (onUploadComplete) {
        onUploadComplete({
          url: uploadUrl,
          originalFile: file,
          uploadTime: new Date(),
          metadata: uploadParams
        });
      }

      if (onAvatarChange) {
        onAvatarChange({
          url: uploadUrl,
          file: null,
          isPreview: false,
          isUploaded: true,
          uploadStatus: 'success',
          hasChanges: true
        });
      }

    } catch (error) {
      console.error('[AVATAR_DUAL] Upload failed:', error);
      dispatch({
        type: 'UPLOAD_ERROR',
        payload: { message: error.message }
      });

      if (onUploadError) {
        onUploadError(error);
      }
    }
  }, [autoUpload, uploadMedia, resourceType, uploadParams, userId, onUploadComplete, onUploadError, onAvatarChange]);

  // Process pending uploads with the new reducer system
  useEffect(() => {
    if (pendingUpload) {
      const { result, uploadId } = pendingUpload;
      console.log('[UPLOAD_PROCESSOR] Processing pending upload:', uploadId);

      generateSignedUrlForUpload(result.url).then(signedUrl => {
        const uploadSlot = activeSlot === 'A' ? 'B' : 'A';
        console.log(`[UPLOAD_PROCESSOR] Loading uploaded image into slot ${uploadSlot}`);

        dispatch({
          type: 'UPLOAD_COMPLETE',
          payload: { targetSlot: uploadSlot, signedUrl }
        });

        // Brief delay to show success message (reduced from 1.5s to 500ms)
        setTimeout(() => {
          dispatch({
            type: 'UPLOAD_FINALIZED',
            payload: { finalSlot: uploadSlot }
          });
        }, 500);
      }).catch(error => {
        console.error('[UPLOAD_PROCESSOR] Error processing upload:', error);
        dispatch({
          type: 'UPLOAD_ERROR',
          payload: { message: error.message }
        });
      });
    }
  }, [pendingUpload, activeSlot, generateSignedUrlForUpload]);

  // Watch for upload status changes from parent
  useEffect(() => {
    console.log('[UPLOAD_STATUS] Upload status changed:', { uploadStatus, uploadResult });

    if (uploadStatus === 'success' && uploadResult) {
      const uploadId = uploadResult.url + '_' + Date.now(); // Create unique ID
      dispatch({
        type: 'UPLOAD_SUCCESS',
        payload: { result: uploadResult, uploadId }
      });
    } else if (uploadStatus === 'error' && uploadResult?.error) {
      dispatch({
        type: 'UPLOAD_ERROR',
        payload: { message: uploadResult.error }
      });
    }
  }, [uploadStatus, uploadResult]);

  // Unified character change and avatar initialization effect
  useEffect(() => {
    const hasCharacterChanged = prevCharacterIdRef.current !== characterId;

    console.log('[AVATAR_UNIFIED] Effect triggered:', {
      characterId,
      hasCharacterChanged,
      currentAvatarUrl: currentAvatarUrl?.substring(0, 50) + '...',
      slotASource: slotA.source?.substring(0, 50) + '...',
      previewUrl: previewUrl?.substring(0, 50) + '...',
      loadingSlot,
      hintMode: hint.mode
    });

    // Handle character change first
    if (hasCharacterChanged) {
      console.log('[AVATAR_UNIFIED] Character changed, performing reset and initialization');

      // Clear preview state FIRST to prevent race conditions
      if (previewUrl && previewUrl.startsWith('blob:')) {
        console.log('[AVATAR_UNIFIED] Revoking blob URL:', previewUrl);
        URL.revokeObjectURL(previewUrl);
      }

      // Clear any pending timeouts
      if (initializationDebounceRef.current) {
        clearTimeout(initializationDebounceRef.current);
        initializationDebounceRef.current = null;
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }

      resetUpload();
      prevCharacterIdRef.current = characterId;

      // Pre-generate signed URL BEFORE reset to eliminate delay
      if (currentAvatarUrl) {
        console.log('[AVATAR_UNIFIED] Pre-generating signed URL for fast loading:', currentAvatarUrl);

        if (isS3Url(currentAvatarUrl) && !isSignedUrl(currentAvatarUrl)) {
          generateSignedUrlForUpload(currentAvatarUrl)
            .then(signedUrl => {
              console.log('[AVATAR_UNIFIED] Signed URL ready, performing fast reset and initialization');
              // Reset with immediate initialization
              dispatch({ type: 'RESET_AVATAR' });
              dispatch({
                type: 'INITIALIZE_AVATAR',
                payload: { avatarUrl: signedUrl, initSlot: 'A' }
              });
            })
            .catch(error => {
              console.error('[AVATAR_UNIFIED] Failed to generate signed URL:', error);
              // Fallback: reset and use original URL
              dispatch({ type: 'RESET_AVATAR' });
              dispatch({
                type: 'INITIALIZE_AVATAR',
                payload: { avatarUrl: currentAvatarUrl, initSlot: 'A' }
              });
            });
        } else {
          // URL doesn't need signing, immediate reset and initialization
          dispatch({ type: 'RESET_AVATAR' });
          dispatch({
            type: 'INITIALIZE_AVATAR',
            payload: { avatarUrl: currentAvatarUrl, initSlot: 'A' }
          });
        }
      } else {
        // No avatar URL, just reset
        dispatch({ type: 'RESET_AVATAR' });
      }
      return;
    }

    // Handle avatar initialization for existing character
    if (!currentAvatarUrl) {
      console.log('[AVATAR_UNIFIED] No currentAvatarUrl provided');
      return;
    }

    // DON'T override if we're showing a preview
    if (previewUrl) {
      console.log('[AVATAR_UNIFIED] Skipping initialization - preview active:', previewUrl);
      return;
    }

    // DON'T override if we're in success mode
    if (hint.mode === 'success') {
      console.log('[AVATAR_UNIFIED] Skipping initialization - in success mode');
      return;
    }

    // DON'T override if we're loading something
    if (loadingSlot) {
      console.log('[AVATAR_UNIFIED] Skipping initialization - loading in progress');
      return;
    }

    // Check if we're already showing the same image (simplified check)
    if (slotA.source === currentAvatarUrl) {
      console.log('[AVATAR_UNIFIED] Skipping initialization - same URL already loaded');
      return;
    }

    // For S3 URLs, check filename match
    if (slotA.source && isS3Url(currentAvatarUrl) && isS3Url(slotA.source)) {
      const currentFileName = currentAvatarUrl.split('/').pop().split('?')[0];
      const currentImageFileName = slotA.source.split('/').pop().split('?')[0];
      if (currentFileName === currentImageFileName) {
        console.log('[AVATAR_UNIFIED] Skipping initialization - same file already loaded:', currentFileName);
        return;
      }
    }

    // Clear previous debounce
    if (initializationDebounceRef.current) {
      clearTimeout(initializationDebounceRef.current);
    }

    // Debounce initialization to prevent rapid re-runs
    initializationDebounceRef.current = setTimeout(async () => {
      console.log('[AVATAR_UNIFIED] Initializing character avatar:', currentAvatarUrl);

      // Generate signed URL if needed
      if (isS3Url(currentAvatarUrl) && !isSignedUrl(currentAvatarUrl)) {
        try {
          console.log('[AVATAR_UNIFIED] Generating signed URL for existing character');
          const signedUrl = await generateSignedUrlForUpload(currentAvatarUrl);
          dispatch({
            type: 'INITIALIZE_AVATAR',
            payload: { avatarUrl: signedUrl, initSlot: 'A' }
          });
        } catch (error) {
          console.error('[AVATAR_UNIFIED] Failed to generate signed URL:', error);
          // Fallback to original URL
          dispatch({
            type: 'INITIALIZE_AVATAR',
            payload: { avatarUrl: currentAvatarUrl, initSlot: 'A' }
          });
        }
      } else {
        dispatch({
          type: 'INITIALIZE_AVATAR',
          payload: { avatarUrl: currentAvatarUrl, initSlot: 'A' }
        });
      }
    }, 100);

    // Cleanup debounce on unmount
    return () => {
      if (initializationDebounceRef.current) {
        clearTimeout(initializationDebounceRef.current);
      }
    };
  }, [characterId, currentAvatarUrl, slotA.source, previewUrl, loadingSlot, hint.mode, resetUpload]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }

      // Clear all timeouts
      if (initializationDebounceRef.current) {
        clearTimeout(initializationDebounceRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [previewUrl]);

  // Size class
  const sizeClass = useMemo(() => {
    switch (size) {
      case 'small': return 'avatar-small';
      case 'mini': return 'avatar-mini';
      case 'large': return 'avatar-large';
      case 'medium':
      default: return 'avatar-medium';
    }
  }, [size]);

  // Render hints using the new hint system
  const renderHints = () => {
    if (!showHints) return null;

    return (
      <div className="avatar-hints-container" style={{ color: hint.color }}>
        <Icon name={hint.icon} loading={hint.loading} size='large' />
        <span className="avatar-hint-text">{hint.message}</span>
      </div>
    );
  };

  return (
    <div className="avatar-main-container">
      {renderHints()}
      <div >
        <div className="avatar-container">
          {/* Slot-Based Dual Image System */}
          <div
            className={`avatar-dual-container ${loadingSlot ? `loading-${loadingSlot.toLowerCase()}` : ''} ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <MemoizedAvatarImage
              imageRef={imageARef}
              src={slotA.source}
              isVisible={slotA.isVisible}
              sizeClass={sizeClass}
              imageType="a"
              onLoad={handleSlotLoad('A')}
              onError={handleSlotError('A')}
            />
            <MemoizedAvatarImage
              imageRef={imageBRef}
              src={slotB.source}
              isVisible={slotB.isVisible}
              sizeClass={sizeClass}
              imageType="b"
              onLoad={handleSlotLoad('B')}
              onError={handleSlotError('B')}
            />
          </div>

          {/* File input */}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileSelect}
            accept="image/*"
            disabled={disabled}
          />

          {/* Upload button */}
          <Button
            className="avatar-button"
            icon
            labelPosition="left"
            onClick={() => fileInputRef.current.click()}
            loading={isUploading}
            disabled={disabled || isUploading}
            color="blue"
            size="small"
            type="button"
          >
            {/* <Icon name="upload" /> */}
            <div style={{ marginLeft: "-35px" }}>{isUploading ? 'Uploading...' : 'Change Avatar'}</div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AvatarPicker, (prevProps, nextProps) => {
  return (
    prevProps.currentAvatarUrl === nextProps.currentAvatarUrl &&
    prevProps.characterId === nextProps.characterId &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.showHints === nextProps.showHints &&
    prevProps.hintLocation === nextProps.hintLocation &&
    prevProps.autoUpload === nextProps.autoUpload &&
    prevProps.uploadStatus === nextProps.uploadStatus &&
    prevProps.uploadResult === nextProps.uploadResult
  );
});
